import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  inject,
  Injector,
  Input,
  OnInit,
  QueryList,
  ViewChildren,
} from '@angular/core';
import { Store } from '@sama/app-state';
import { assert } from '@sama/js-core';
import { DirectiveSuperclass } from '@sama/ng-core';
import { Observable } from 'rxjs';

import { shellConnectorToken } from '../../../../team-apis/core/shell-connector';
import { ColorSelectTool } from '../../raster-tools/color-select-tool/color-select-tool.service';
import { ImageContentStore } from '../../state/image-content-store';
import { TagOptionState } from '../../state/tag-option-state';
import { toRgbDomString } from '../../utils/color-utils';

@Component({
  selector: 'h3-tag-option-items',
  templateUrl: './tag-option-items.component.html',
  styleUrls: ['./tag-option-items.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class TagOptionItemsComponent
  extends DirectiveSuperclass
  implements OnInit, AfterViewInit
{
  @Input() collapsed = true;
  @ViewChildren('tagOptionRef')
  tagOptionElements!: QueryList<ElementRef<Element>>;
  tagOptions$!: Observable<TagOptionState[]>;
  selectTagOptionCodeStore!: Store<number>;
  outputId!: number;

  private store = inject(ImageContentStore);
  private shellConnector = inject(shellConnectorToken);
  private colorSelectTool = inject(ColorSelectTool);

  constructor(
    private changeDetectorRef: ChangeDetectorRef,
    injector: Injector,
  ) {
    super(injector);
  }

  get selectedTagOption(): number {
    return this.selectTagOptionCodeStore.state();
  }

  set selectedTagOption(code: number) {
    // For some reason, this method is called several times even if the code has
    // not changed
    if (code === this.selectTagOptionCodeStore.state()) {
      return;
    }
    this.selectTagOptionCodeStore.set(code);
    const label = this.store
      .state()
      .tagOptions.find((tagOption) => tagOption.code === code)!.label;

    this.shellConnector.trackEvent({
      id: 'select_raster_coding_layer_label',
      bucket: 'tag_selection',
      label,
      code,
      source: 'tag_list',
    });
  }

  ngOnInit() {
    this.tagOptions$ = this.store('tagOptions').$;
    this.selectTagOptionCodeStore = this.store('selectedTagOptionCode');
    this.outputId = this.store.state().output.id;
  }

  ngAfterViewInit() {
    this.subscribeToColorSelectedWithEditTool();
  }

  highlight(code?: number) {
    this.store('highlightedCode').set(code);
  }

  getDomColorString(tagOption: TagOptionState) {
    return toRgbDomString(tagOption.rgb);
  }

  private subscribeToColorSelectedWithEditTool() {
    this.subscribeTo(this.colorSelectTool.getRevealColor$(), (code) => {
      const revealedTagOption = this.tagOptionElements.find(
        (item) =>
          item.nativeElement.getAttribute('data-tag-option-code') ===
          code.toString(),
      );

      assert(
        revealedTagOption,
        'the edit tool should never emit a non existent color',
      );

      revealedTagOption.nativeElement.scrollIntoView({ block: 'nearest' });

      // This is required to force the radio buttons to reevaluate
      // selectedTagOption and know if they are selected or not anymore
      this.changeDetectorRef.detectChanges();
    });
  }
}
